import { useState } from "react";
import type { SymptomSummary } from "../../../types/gpSummary";
import Card from "./Card";

interface KeySymptomsCardProps {
  symptoms: SymptomSummary[];
}

const SEVERITY_DOT_COLORS: Record<SymptomSummary["severity"], string> = {
  high: "#C97C7C",
  moderate: "#D4A842",
  low: "#6C7A52",
  none: "#d6d3d1",
};

function StatusBadge({ status }: { status: SymptomSummary["status"] }) {
  const styles: Record<string, { bg: string; color: string; label: string }> = {
    worsening: { bg: "#F5E8E8", color: "#C97C7C", label: "\u2193 Worsening" },
    improving: { bg: "#f0f6e4", color: "#8a9f60", label: "\u2191 Improving" },
    resolved: { bg: "#e9f0dc", color: "#6C7A52", label: "\u2713 Resolved" },
    stable: { bg: "#f5f3ef", color: "#666", label: "\u2192 Stable" },
  };
  const s = styles[status] ?? styles.stable;
  return (
    <span
      className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

function SymptomRow({ symptom }: { symptom: SymptomSummary }) {
  const { name, percent, from, to, status, severity } = symptom;
  const showProgressBar = status === "improving";

  return (
    <div className="mb-4 last:mb-0">
      <div className="flex justify-between items-center mb-1.5">
        <div className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ background: SEVERITY_DOT_COLORS[severity] }}
          />
          <span className="text-sm font-semibold text-stone-700">{name}</span>
        </div>
        <StatusBadge status={status} />
      </div>

      {showProgressBar && (
        <div className="pl-[18px] mb-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-stone-400">{from} {"\u2192"} {to}</span>
            <span className="text-xs font-bold" style={{ color: "#6C7A52" }}>
              {percent}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${percent}%`,
                background: "linear-gradient(90deg,#C6D8A5,#6C7A52)",
              }}
            />
          </div>
        </div>
      )}

      {!showProgressBar && (
        <div className="pl-[18px]">
          <span className="text-xs text-stone-400">{from} {"\u2192"} {to}</span>
        </div>
      )}
    </div>
  );
}

export default function KeySymptomsCard({ symptoms }: KeySymptomsCardProps) {
  const [showResolved, setShowResolved] = useState(false);

  if (symptoms.length === 0) {
    return (
      <Card title="Key Symptoms">
        <p className="text-sm text-stone-400 italic">No symptom progression data available yet.</p>
      </Card>
    );
  }

  // Group A: Needs Attention (worsening + stable at high severity)
  const needsAttention = symptoms.filter(
    (s) => s.status === "worsening" || (s.status === "stable" && s.severity === "high")
  );

  // Group B: Progressing (improving + stable not-high)
  const progressing = symptoms.filter(
    (s) => s.status === "improving" || (s.status === "stable" && s.severity !== "high")
  );

  // Group C: Resolved
  const resolved = symptoms.filter((s) => s.status === "resolved");

  return (
    <Card title="Key Symptoms">
      {needsAttention.length > 0 && (
        <div className="mb-5 border-l-2 border-red-100 pl-3">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-3">
            Needs Attention
          </p>
          {needsAttention.map((s) => (
            <SymptomRow key={s.name} symptom={s} />
          ))}
        </div>
      )}

      {progressing.length > 0 && (
        <div className="mb-5">
          {needsAttention.length > 0 && (
            <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-3">
              Progressing
            </p>
          )}
          {progressing.map((s) => (
            <SymptomRow key={s.name} symptom={s} />
          ))}
        </div>
      )}

      {resolved.length > 0 && (
        <div>
          <button
            onClick={() => setShowResolved(!showResolved)}
            className="flex items-center gap-2 text-xs font-semibold text-stone-500 hover:text-stone-700 transition-colors"
          >
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-xs"
              style={{ background: "#e9f0dc", color: "#6C7A52" }}
            >
              {resolved.length}
            </span>
            <span>{resolved.length} symptom{resolved.length !== 1 ? "s" : ""} resolved</span>
            <span className="text-stone-400">{showResolved ? "\u25B2" : "\u25BC"}</span>
          </button>

          {showResolved && (
            <div className="mt-3">
              {resolved.map((s) => (
                <div key={s.name} className="flex items-center gap-2 py-1.5">
                  <span style={{ color: "#6C7A52" }}>{"\u2713"}</span>
                  <span className="text-sm text-stone-600">{s.name}</span>
                  <span className="text-xs text-stone-400 ml-auto">{s.from} {"\u2192"} {s.to}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
